import {
	type GetAdminReceivablesQuery,
	ReceivableAgeBucket,
	useGetAdminReceivablesQuery,
} from '@codezo/graphql';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { money, percentDelta } from './performanceFormat';

type Receivables = GetAdminReceivablesQuery['getAdminReceivables'];

const AGE_META: Record<ReceivableAgeBucket, { label: string; bar: string; ink: string }> = {
	[ReceivableAgeBucket.DueSoon]: { label: 'Due in 7 days', bar: 'bg-text-3', ink: 'text-text-2' },
	[ReceivableAgeBucket.DueLater]: { label: 'Due later', bar: 'bg-text-4', ink: 'text-text-2' },
	[ReceivableAgeBucket.Late_1_30]: {
		label: '1–30 days late',
		bar: 'bg-status-warn',
		ink: 'text-status-warn',
	},
	[ReceivableAgeBucket.Late_31Plus]: {
		label: '30+ days late',
		bar: 'bg-status-danger',
		ink: 'text-status-danger',
	},
};

const LATE = [ReceivableAgeBucket.Late_1_30, ReceivableAgeBucket.Late_31Plus];
const invoices = (n: number) => `${n} ${n === 1 ? 'invoice' : 'invoices'}`;

const monthName = new Intl.DateTimeFormat('en-GB', { month: 'long' });
const previousMonthName = () => {
	const d = new Date();
	return monthName.format(new Date(d.getFullYear(), d.getMonth() - 1, 1));
};

function Stat({
	label,
	value,
	sub,
	first,
}: {
	label: string;
	value: string;
	sub: React.ReactNode;
	first?: boolean;
}) {
	return (
		<div
			className={cn('flex min-w-0 flex-col gap-1.5 p-4', !first && 'border-l border-line-1')}
		>
			<span className="text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
				{label}
			</span>
			<span className="text-xl leading-tight font-normal -tracking-[0.02em] whitespace-nowrap text-text-1 tabular-nums">
				{value}
			</span>
			<span className="truncate text-xs text-text-3 tabular-nums">{sub}</span>
		</div>
	);
}

function Body({ data }: { data: Receivables }) {
	const s = data.summary!;
	const cur = data.currency;
	const change = percentDelta(s.invoiced, s.previousInvoiced);
	return (
		<>
			<div className="grid grid-cols-3 border-b border-line-1">
				<Stat
					first
					label="Invoiced"
					value={money(s.invoiced, cur)}
					sub={
						s.invoiced === 0 ? (
							'None yet this month'
						) : change ? (
							<>
								<span
									className={
										change.tone === 'down'
											? 'text-status-danger'
											: 'text-status-success'
									}
								>
									{change.text}
								</span>{' '}
								vs {previousMonthName()}
							</>
						) : (
							`${s.invoicedCount} ${s.invoicedCount === 1 ? 'invoice' : 'invoices'}`
						)
					}
				/>
				<Stat
					label="Collected"
					value={s.collectionRate != null ? `${Math.round(s.collectionRate)}%` : '—'}
					sub={`${money(s.collected, cur)} paid`}
				/>
				<Stat
					label="Avg. days to pay"
					value={
						s.averageDaysToPay == null
							? '—'
							: s.averageDaysToPay < 0.5
								? 'Same day'
								: `${Math.round(s.averageDaysToPay)} ${Math.round(s.averageDaysToPay) === 1 ? 'day' : 'days'}`
					}
					sub={
						s.paidInvoiceCount
							? `${s.paidInvoiceCount} paid · last 90 days`
							: 'None paid in 90 days'
					}
				/>
			</div>

			{s.outstanding > 0 || s.overdueCount > 0 ? (
				<>
					{/* Outstanding by age: one row per bucket, bar against everything owed. */}
					<div className="border-b border-line-1 px-4 pt-3 pb-2">
						<div className="flex items-baseline gap-2 pb-1.5">
							<span className="flex-1 text-base text-text-2">Outstanding by age</span>
							<span className="text-base text-text-1 tabular-nums">
								{money(s.outstanding, cur)}
							</span>
							<span className="text-xs text-text-4 tabular-nums">
								{invoices(s.aging.reduce((n, a) => n + a.count, 0))}
							</span>
						</div>
						{s.aging.map((a) => {
							const share = s.outstanding > 0 ? (a.amount / s.outstanding) * 100 : 0;
							return (
								<div
									key={a.bucket}
									className="grid grid-cols-[120px_minmax(0,1fr)_96px_36px] items-center gap-4 py-1.5"
								>
									<span className="flex flex-col">
										<span
											className={cn(
												'text-base',
												LATE.includes(a.bucket) && a.amount > 0
													? AGE_META[a.bucket].ink
													: 'text-text-2'
											)}
										>
											{AGE_META[a.bucket].label}
										</span>
										<span className="text-xs text-text-4 tabular-nums">
											{invoices(a.count)}
										</span>
									</span>
									<span className="h-1 overflow-hidden rounded-full bg-line-1">
										<span
											className={cn(
												'block h-full rounded-full',
												AGE_META[a.bucket].bar
											)}
											style={{ width: `${share}%` }}
										/>
									</span>
									<span
										className={cn(
											'text-right text-sm tabular-nums',
											LATE.includes(a.bucket) && a.amount > 0
												? AGE_META[a.bucket].ink
												: 'text-text-1'
										)}
									>
										{money(a.amount, cur)}
									</span>
									<span className="text-right text-xs text-text-4 tabular-nums">
										{Math.round(share)}%
									</span>
								</div>
							);
						})}
					</div>

					<div className="flex h-10 items-center border-b border-line-1 px-4 text-base">
						<span className="flex-1 text-text-2">Overdue invoices</span>
						<span
							className={cn(
								'tabular-nums',
								s.overdueCount ? 'text-status-danger' : 'text-text-3'
							)}
						>
							{money(
								s.aging
									.filter((a) => LATE.includes(a.bucket))
									.reduce((n, a) => n + a.amount, 0),
								cur
							)}
						</span>
					</div>
					{s.overdue.length === 0 ? (
						<div className="px-4 py-6 text-center text-sm text-text-3">
							Nothing overdue.
						</div>
					) : (
						s.overdue.map((o) => (
							<Link
								key={o.invoiceNumber}
								to={`/orders/${o.orderId}`}
								title={o.order ? `Order ${o.order.orderNumber}` : undefined}
								className="group grid h-11 grid-cols-[minmax(0,1fr)_auto_auto_96px_14px] items-center gap-3 border-b border-line-1 px-4 transition-colors hover:bg-surface-hover"
							>
								<span className="truncate text-base font-medium text-text-1">
									{o.order?.organization?.name ?? 'Unknown client'}
								</span>
								<span className="font-mono text-xs text-text-4">
									INV-{o.invoiceNumber}
								</span>
								<span
									className={cn(
										'rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap tabular-nums',
										o.daysLate > 30
											? 'bg-status-danger-soft text-status-danger'
											: 'bg-status-warn-soft text-status-warn'
									)}
								>
									{o.daysLate} {o.daysLate === 1 ? 'day' : 'days'}
								</span>
								<span className="text-right font-mono text-sm text-text-1">
									{money(o.remainder, cur)}
								</span>
								<ChevronRight className="size-3.5 text-text-4 transition-colors group-hover:text-text-2" />
							</Link>
						))
					)}
				</>
			) : (
				// Nothing owed: one line instead of four empty buckets and an empty overdue list.
				<div className="flex items-center gap-2 px-4 py-6 text-sm text-text-3">
					<span className="size-1.5 rounded-full bg-status-success" />
					Nothing outstanding — every booked invoice is paid.
				</div>
			)}
		</>
	);
}

/**
 * What clients owe on bank-transfer invoices, read from e-conomic. Display only — matching a payment
 * to an invoice is done in e-conomic, and the dashboard follows it on the next sync.
 */
export function PaymentsCard() {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, error, refetch } = useGetAdminReceivablesQuery({
		variables: { currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});
	const r = data?.getAdminReceivables;
	const hidden = r?.summary ? r.summary.overdueCount - r.summary.overdue.length : 0;

	return (
		<section
			className="flex flex-col overflow-hidden rounded-xl border border-line-1 bg-surface"
			aria-labelledby="perf-pay-h"
		>
			<div className="flex h-11 items-center gap-2 border-b border-line-1 px-4">
				<h2
					id="perf-pay-h"
					className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
				>
					Payments
				</h2>
				<span className="text-sm text-text-4">{monthName.format(new Date())}</span>
				<span className="flex-1" />
				<span className="text-xs text-text-4">Bank-transfer invoices · excl. VAT</span>
			</div>

			{loading && !r && <div className="h-[320px] animate-pulse" />}
			{error && !r && (
				<div className="flex flex-1 items-center justify-center gap-3 px-4 py-10 text-sm text-text-3">
					Couldn’t load payments.
					<button
						type="button"
						onClick={() => refetch()}
						className="text-text-1 hover:text-accent"
					>
						Retry
					</button>
				</div>
			)}
			{r && <Body data={r} />}

			<div className="mt-auto flex h-10 items-center gap-2 px-4 text-xs text-text-4">
				<span className="flex-1">Payment matching happens in e-conomic</span>
				{hidden > 0 && <span className="tabular-nums">+{hidden} more overdue</span>}
			</div>
		</section>
	);
}
